<div class="container-fluid mt-3">
  <div class="card">
    <!-- FIXED HEADER WITH PROPER BUTTON ALIGNMENT -->
    <div class="card-header d-flex justify-content-between align-items-center mb-2">
      <div class="d-flex align-items-center gap-2">
        <ng-container *ngIf="(displaySource$ | async) as source">
        <span *ngIf="source === 'global-consolidated'" class="badge bg-secondary">
          {{ totalTestsCount$ | async }} Final Tests
        </span>
          <span *ngIf="source === 'generated'" class="badge bg-secondary">
          {{ totalRawTestsCount$ | async }} Raw Tests
        </span>
        </ng-container>
      </div>
      <div class="d-flex align-items-center gap-2">
        <!-- Export Buttons -->
        <div *ngIf="(exportButtonsVisible$ | async)" class="btn-group mr-2" role="group">
          <button class="btn btn-sm btn-outline-secondary" (click)="downloadConsolidatedCSV()">Export CSV</button>
          <button class="btn btn-sm btn-outline-secondary" (click)="downloadConsolidatedExcel()">Export Excel</button>
        </div>

        <!-- CONSOLIDATION BUTTON -->
        <button class="btn btn-sm btn-success mr-2"
                (click)="startGlobalConsolidation()"
                [disabled]="isGrouping || isConsolidating"
                *ngIf="(displaySource$ | async) === 'generated' && (generatedTests$ | async)?.length > 0">
          <span *ngIf="isGrouping || isConsolidating" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
          {{ isGrouping ? 'Grouping...' : (isConsolidating ? 'Consolidating...' : 'Consolidate All Tests') }}
        </button>

        <!-- VIEW TOGGLE BUTTONS GROUP -->
        <div class="btn-group" role="group">
          <!-- View Source Toggle Button -->
          <button class="btn btn-sm btn-outline-info mr-2" (click)="toggleDisplaySource()">
            {{ (displaySource$ | async) === 'global-consolidated' ? 'View Raw Tests' : 'View Final Tests' }}
          </button>

          <!-- FIXED: Consolidated View Toggle (only show when viewing consolidated tests) -->
          <button *ngIf="(displaySource$ | async) === 'global-consolidated'"
                  class="btn btn-sm btn-outline-primary"
                  (click)="toggleConsolidatedView()">
            {{ showAllConsolidatedView ? 'View by Macro Scenario' : 'View All Consolidated Tests' }}
          </button>

          <!-- Generated View Type Toggle Button (only show when viewing raw tests) -->
          <button *ngIf="(displaySource$ | async) === 'generated'"
                  class="btn btn-sm btn-outline-primary"
                  (click)="toggleGeneratedView()">
            {{ showAllGeneratedView ? 'View by Macro Scenario' : 'View All Raw Tests' }}
          </button>
        </div>
      </div>
    </div>

    <!-- PROGRESS BAR -->
    <div *ngIf="isConsolidating" class="progress" style="height: 5px;">
      <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
           [style.width]="(consolidationProgress.current / (consolidationProgress.total || 1)) * 100 + '%'"></div>
    </div>

    <div class="card-body">
      <!-- GLOBAL CONSOLIDATED TESTS VIEW (UPDATED WITH FIXES) -->
      <ng-container *ngIf="(displaySource$ | async) === 'global-consolidated'">
        <div *ngIf="(globalConsolidatedTests$ | async) as finalTests; else loadingConsolidated">
          <div *ngIf="!finalTests || finalTests.length === 0" class="alert alert-info">
            No final consolidated tests available. Run the "Consolidate All Tests" process from the Raw Tests view.
          </div>
          <div *ngIf="finalTests && finalTests.length > 0">
            <!-- Grouped by Macro Scenario View (Consolidated) -->
            <div *ngIf="!showAllConsolidatedView">
              <div *ngFor="let group of (groupedConsolidatedTestsWithSequentialIds$ | async)" class="mb-4">
                <app-criterion
                  [macroScenarioData]="group.macro"
                  [displayMode]="'macroHeader'">
                </app-criterion>

                <!-- FIXED: Better styled consolidation summary -->
                <div class="d-flex align-items-center mb-2 p-2"
                     style="background-color: #f8f9fa; border-left: 4px solid #28a745; border-radius: 0.25rem;">
                  <i class="fas fa-compress-arrows-alt text-success me-2"></i>
                  <small class="mb-0">
                    <strong>Consolidation:</strong>
                    <span class="text-primary ml-1">{{ group.originalRawTestsCount }} raw tests</span>
                    →
                    <span class="text-success">{{ group.consolidatedTests.length }} final tests</span>
                    <span *ngIf="group.consolidatedTests.length < group.originalRawTestsCount" class="text-success fw-bold">
                      ({{ calculateReductionPercentage(group.originalRawTestsCount, group.consolidatedTests.length) }}% reduction)
                    </span>
                    <span *ngIf="group.consolidatedTests.length >= group.originalRawTestsCount" class="text-muted">
                      (no reduction)
                    </span>
                  </small>
                </div>

                <ng-container *ngIf="group.consolidatedTests.length > 0; else noConsolidatedTestsForThisMacro">
                  <app-test-table
                    [tests]="group.consolidatedTests"
                    [columns]="displayableColumns$ | async"
                    [showMicroScenarioColumn]="false"
                    [isSimplifiedDisplayMode]="true"
                    [showActions]="true"
                    (testUpdated)="handleTestUpdate($event)"
                    (testDeleted)="handleTestDelete($event)">
                  </app-test-table>
                </ng-container>

                <ng-template #noConsolidatedTestsForThisMacro>
                  <div class="alert alert-warning mt-2" style="border-left: 4px solid #ffc107;">
                    <i class="fas fa-exclamation-triangle me-2"></i>
                    <strong>No consolidated tests</strong> for macro scenario: {{ group.macro.scenario_title }}
                    <span *ngIf="group.originalRawTestsCount > 0">
                      <br><small class="text-muted">
                        The {{ group.originalRawTestsCount }} original raw tests may have been consolidated into other macro scenarios.
                      </small>
                    </span>
                  </div>
                </ng-template>
              </div>
            </div>

            <!-- All Consolidated Tests (Flat) View -->
            <div *ngIf="showAllConsolidatedView">
              <div *ngIf="(totalTestsCount$ | async) > 0">
                <!-- ======== TYPE FILTER BUTTONS ======== -->
                <div class="mb-3">
                  <div class="btn-group" role="group" aria-label="Filter by test type">
                    <button type="button" class="btn btn-sm" [ngClass]="currentTestTypeFilter === null ? 'btn-primary' : 'btn-outline-primary'" (click)="applyTestTypeFilter(null)">
                      All
                    </button>
                    <button type="button" class="btn btn-sm" *ngFor="let type of testTypesWithCounts$ | async" [ngClass]="currentTestTypeFilter === type.name ? 'btn-primary' : 'btn-outline-primary'" (click)="applyTestTypeFilter(type.name)">
                      {{ type.name }} <span class="badge bg-light text-dark ms-1">{{ type.count }}</span>
                    </button>
                  </div>
                </div>

                <!-- ======== NEW REVIEW STATUS FILTER & KPIs START ======== -->
                <div class="mb-3 p-2 rounded" style="background-color: #f8f9fa;">
                  <div class="d-flex justify-content-between align-items-center">
                    <!-- Filter Buttons -->
                    <div class="btn-group" role="group" aria-label="Filter by review status">
                      <button type="button" class="btn btn-sm" [ngClass]="currentReviewFilter === 'all' ? 'btn-info' : 'btn-outline-info'" (click)="applyReviewFilter('all')">
                        All
                      </button>
                      <button type="button" class="btn btn-sm" [ngClass]="currentReviewFilter === 'review' ? 'btn-warning' : 'btn-outline-warning'" (click)="applyReviewFilter('review')">
                        To Review <span class="badge bg-light text-dark ms-1">{{ testsToReviewCount$ | async }}</span>
                      </button>
                      <button type="button" class="btn btn-sm" [ngClass]="currentReviewFilter === 'non-review' ? 'btn-success' : 'btn-outline-success'" (click)="applyReviewFilter('non-review')">
                        Reviewed <span class="badge bg-light text-dark ms-1">{{ testsWithoutReviewCount$ | async }}</span>
                      </button>
                    </div>
                  </div>
                </div>
                <!-- ======== NEW REVIEW STATUS FILTER & KPIs END ======== -->
              </div>

              <!-- The test table now uses the observable that listens to ALL filters -->
              <app-test-table
                [tests]="filteredConsolidatedTests$ | async"
                [columns]="displayableColumns$ | async"
                [showMicroScenarioColumn]="false"
                [isSimplifiedDisplayMode]="true"
                [showActions]="true"
                (testUpdated)="handleTestUpdate($event)"
                (testDeleted)="handleTestDelete($event)">
              </app-test-table>
            </div>
          </div>
        </div>
        <ng-template #loadingConsolidated>
          <p>Loading consolidated test data...</p>
        </ng-template>
      </ng-container>

      <!-- GENERATED (RAW) TESTS VIEW -->
      <ng-container *ngIf="(displaySource$ | async) === 'generated'">
        <div *ngIf="(generatedTests$ | async) as genTests; else loadingGeneratedTests">
          <div *ngIf="!genTests || genTests.length === 0" class="alert alert-info">
            No Raw Tests available to display.
          </div>

          <!-- Grouped by Macro Scenario View (Generated) WITH SEQUENTIAL IDs -->
          <div *ngIf="genTests && genTests.length > 0 && !showAllGeneratedView">
            <h5>Raw Tests by Macro Scenario</h5>
            <!-- CHANGE FROM: (groupedGeneratedTestsData$ | async) -->
            <!-- TO: (groupedGeneratedTestsWithSequentialIds$ | async) -->
            <div *ngFor="let group of (groupedGeneratedTestsWithSequentialIds$ | async)" class="mb-4">
              <app-criterion
                [macroScenarioData]="group.macro"
                [displayMode]="'macroHeader'">
              </app-criterion>
              <ng-container *ngIf="group.tests.length > 0; else noTestsForThisMacroGenerated">
                <app-test-table
                  [tests]="group.tests"
                  [columns]="displayableColumns$ | async"
                  [showMicroScenarioColumn]="false"
                  [isSimplifiedDisplayMode]="true"
                  [showActions]="true"
                  (testUpdated)="handleTestUpdate($event)"
                  (testDeleted)="handleTestDelete($event)">
                </app-test-table>
              </ng-container>
              <ng-template #noTestsForThisMacroGenerated>
                <div class="alert alert-sm alert-secondary mt-2">No Unique Raw Tests found for this macro scenario: {{ group.macro.scenario_title }}.
                  Shared Tests are present in other Macroscenarios.</div>
              </ng-template>
            </div>
          </div>

          <!-- All Raw Tests (Flat) View -->
          <div *ngIf="genTests && genTests.length > 0 && showAllGeneratedView">

            <!-- ======== NEW RAW TEST FILTER BUTTONS START ======== -->
            <div *ngIf="(totalRawTestsCount$ | async) as totalRawTestsCount" class="mb-3">
              <div *ngIf="totalRawTestsCount > 0">
                <div class="btn-group" role="group" aria-label="Filter by raw test type">
                  <!-- "All" Button -->
                  <button type="button" class="btn btn-sm"
                          [ngClass]="currentRawTestTypeFilter === null ? 'btn-primary' : 'btn-outline-primary'"
                          (click)="applyRawTestTypeFilter(null)">
                    All <span class="badge bg-light text-dark ms-1">{{ totalRawTestsCount }}</span>
                  </button>
                  <!-- Buttons for each test type -->
                  <button type="button" class="btn btn-sm"
                          *ngFor="let type of rawTestTypesWithCounts"
                          [ngClass]="currentRawTestTypeFilter === type.name ? 'btn-primary' : 'btn-outline-primary'"
                          (click)="applyRawTestTypeFilter(type.name)">
                    {{ type.name }} <span class="badge bg-light text-dark ms-1">{{ type.count }}</span>
                  </button>
                </div>
              </div>
            </div>
            <!-- ======== NEW RAW TEST FILTER BUTTONS END ======== -->

            <!-- The test table now uses the NEW filtered observable for raw tests -->
            <app-test-table
              [tests]="filteredRawTests$ | async"
              [columns]="displayableColumns$ | async"
              [showMicroScenarioColumn]="false"
              [isSimplifiedDisplayMode]="true"
              [showActions]="true"
              (testUpdated)="handleTestUpdate($event)"
              (testDeleted)="handleTestDelete($event)">
            </app-test-table>
          </div>
        </div>
        <ng-template #loadingGeneratedTests>
          <p>Loading Raw Tests...</p>
        </ng-template>
      </ng-container>
    </div>
  </div>
</div>
